import { CallGrensEditor } from "@/components/CallGrensEditor";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ContactEditor } from "@/components/ContactEditor";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Save, KeyRound, Pencil, ShieldCheck, User, ScrollText, Activity, AlertTriangle, Sparkles, RefreshCw, GitPullRequest, Check, X, ArrowRightLeft } from "lucide-react";
import { LoadError, errMessage } from "@/components/LoadError";
import { Systeemcheck } from "@/components/Systeemcheck";
import { VerzoekItem } from "@/components/WeekritmeRequestDialog";
import { AfdelingenEditor } from "@/components/afdelingen/AfdelingenEditor";
import { TijdelijkWachtwoordDialog } from "@/components/TijdelijkWachtwoord";
import { AfdelingenWijzig } from "@/components/afdelingen/AfdelingenWijzig";
import { toast } from "sonner";

const DAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const TYPES = [{ v: "taak", l: "Taak" }, { v: "discipline", l: "Discipline" }, { v: "rooster", l: "Rooster" }];

const input = "w-full px-3 py-2 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm text-zinc-900";
const btnPrimary = "flex items-center gap-1.5 px-4 py-2 rounded-md bg-zinc-950 text-white text-sm font-semibold uppercase tracking-wider hover:bg-zinc-800 transition-colors disabled:opacity-60";
const btnGhost = "flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-300 text-sm font-medium text-zinc-700 hover:border-zinc-400 transition-colors";
const sectionTitle = "font-heading text-lg font-bold text-zinc-900 mb-1";
const sectionDesc = "text-sm text-zinc-500 mb-4";
const card = "bg-white rounded-[10px] border border-zinc-200 p-4 md:p-5";

function err(e, fallback) { toast.error(e?.response?.data?.detail || fallback); }

export default function Settings() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [confirm, setConfirm] = useState(null); // {title, desc, onConfirm}
  const [metaError, setMetaError] = useState("");
  const [usersError, setUsersError] = useState("");

  const loadMeta = () => api.get("/meta").then((r) => { setMeta(r.data); setMetaError(""); }).catch((e) => setMetaError(errMessage(e)));
  const loadUsers = () => api.get("/users").then((r) => { setUsers(r.data); setUsersError(""); }).catch((e) => setUsersError(errMessage(e)));
  useEffect(() => { loadMeta(); loadUsers(); }, []);

  if (!meta) return (
    <div data-testid="instellingen-page" className="space-y-3">
      {metaError ? <LoadError error={metaError} onRetry={loadMeta} testid="instellingen-load-error" /> : <div className="text-sm text-zinc-500">Laden…</div>}
    </div>
  );

  return (
    <div data-testid="instellingen-page">
      <div className="mb-3 md:mb-4">
        <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Instellingen</h1>
        <p className="text-sm text-zinc-500">Beheer de inhoud en regels van de app. Alles wordt server-side opgeslagen.</p>
      </div>

      {usersError && <LoadError error={usersError} onRetry={loadUsers} testid="instellingen-users-error" className="mb-4" />}

      <Tabs defaultValue="locatie-call">
        <TabsList className="mb-3 md:mb-4 flex-wrap h-auto">
          <TabsTrigger value="locatie-call" data-testid="tab-locatie-call">Locatie-call</TabsTrigger>
          <TabsTrigger value="weekritme" data-testid="tab-weekritme">Weekritme</TabsTrigger>
          <TabsTrigger value="verzoeken" data-testid="tab-verzoeken">Verzoeken</TabsTrigger>
          <TabsTrigger value="locaties" data-testid="tab-locaties">Locatielijst</TabsTrigger>
          <TabsTrigger value="gebruikers" data-testid="tab-gebruikers">Gebruikers</TabsTrigger>
          <TabsTrigger value="afdelingen" data-testid="tab-afdelingen">Afdelingen</TabsTrigger>
          <TabsTrigger value="activiteit" data-testid="tab-activiteit">Activiteit</TabsTrigger>
          <TabsTrigger value="ai-analyse" data-testid="tab-ai-analyse">AI-analyse</TabsTrigger>
          <TabsTrigger value="logboek" data-testid="tab-logboek">Logboek</TabsTrigger>
          <TabsTrigger value="systeemcheck" data-testid="tab-systeemcheck">Systeemcheck</TabsTrigger>
        </TabsList>

        <TabsContent value="locatie-call" className="space-y-5">
          <TextsEditor meta={meta} onSaved={loadMeta} />
          <PrepWindowEditor key={`${meta.configVersions?.callDag}-${meta.configVersions?.prepWindow}`} meta={meta} onSaved={loadMeta} />
          <KpiEditor meta={meta} onSaved={loadMeta} />
          <PrepEditor meta={meta} onSaved={loadMeta} />
          <BezoekChecksEditor meta={meta} onSaved={loadMeta} />
        </TabsContent>

        <TabsContent value="weekritme">
          <TaskEditor meta={meta} users={users} onSaved={loadMeta} />
        </TabsContent>

        <TabsContent value="verzoeken">
          <VerzoekenBeheer onApplied={loadMeta} />
        </TabsContent>

        <TabsContent value="locaties">
          <LocationEditor meta={meta} onSaved={loadMeta} setConfirm={setConfirm} />
        </TabsContent>

        <TabsContent value="gebruikers">
          <UserEditor users={users} me={user} meta={meta} onSaved={loadUsers} onMetaSaved={loadMeta} setConfirm={setConfirm} />
        </TabsContent>

        <TabsContent value="afdelingen">
          <AfdelingenEditor meta={meta} onSaved={loadMeta} />
        </TabsContent>

        <TabsContent value="activiteit">
          <ActivityOverview />
        </TabsContent>

        <TabsContent value="ai-analyse">
          <AiAnalysis meta={meta} />
        </TabsContent>

        <TabsContent value="logboek">
          <AuditLog />
        </TabsContent>
        <TabsContent value="systeemcheck">
          <Systeemcheck />
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Annuleren</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-ok" className="bg-rose-600 hover:bg-rose-700"
              onClick={() => { confirm?.onConfirm?.(); setConfirm(null); }}>{confirm?.confirmLabel || "Bevestigen"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TextsEditor({ meta, onSaved }) {
  const [t, setT] = useState({ masterBasics: "", repeatQuestion: "", wowLabel: "", ...meta.texts });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put("/config/texts", { value: t, baseVersion: meta.configVersions?.texts }); toast.success("Teksten opgeslagen"); onSaved(); }
    catch (e) {
      if (e?.response?.status === 409) { toast.error(e.response.data.detail); onSaved(); }
      else err(e, "Opslaan mislukt");
    } finally { setSaving(false); }
  };
  return (
    <div className={card} data-testid="texts-editor">
      <h2 className={sectionTitle}>Teksten & banners</h2>
      <p className={sectionDesc}>De "Master the basics"-banner, de afsluitende herhaalvraag en het WOW-label.</p>
      <div className="space-y-3">
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-1">Master the basics-banner</label>
          <textarea data-testid="text-masterBasics" rows={2} className={input} value={t.masterBasics} onChange={(e) => setT({ ...t, masterBasics: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-1">Herhaalvraag (afsluiting)</label>
          <input data-testid="text-repeatQuestion" className={input} value={t.repeatQuestion} onChange={(e) => setT({ ...t, repeatQuestion: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-1">WOW-label</label>
          <input data-testid="text-wowLabel" className={input} value={t.wowLabel} onChange={(e) => setT({ ...t, wowLabel: e.target.value })} />
        </div>
      </div>
      <button className={`${btnPrimary} mt-4`} data-testid="save-texts" onClick={save} disabled={saving}><Save className="h-4 w-4" />{saving ? "Opslaan…" : "Opslaan"}</button>
    </div>
  );
}

const DAGNAMEN = ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"];
const MND = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const vanIso = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };
const plusDagen = (dt, n) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() + n);
const maDag = (dt) => (dt.getDay() + 6) % 7;
const kortLabel = (dt) => `${DAGNAMEN[maDag(dt)].slice(0, 2)} ${dt.getDate()} ${MND[dt.getMonth()]}`;

// v15 B3: calldag (met "geldt vanaf") + aanlevervenster in dagen vóór de call, met live het moment voor de eerstvolgende call.
function PrepWindowEditor({ meta, onSaved }) {
  const cd = meta.callDag || { dag: 0, historieRegels: [], vanafOpties: [] };
  const [dag, setDag] = useState(cd.dagVolgendeWeek ?? cd.dag);   // R2: de dag van volgende week (anders overschrijft opslaan een geplande wijziging)
  const [vanaf, setVanaf] = useState("volgende");
  const [w, setW] = useState({ openDaysBefore: 7, openTime: "00:00", closeDaysBefore: 0, closeTime: "09:00", ...meta.prepWindowConfig });
  const [saving, setSaving] = useState(false);
  const venster = meta.prepWindow;
  // R2: de voorvertoning volgt de nog niet opgeslagen calldag als die de eerstvolgende call raakt
  const vc = cd.volgendeCall;
  const keuze = cd.vanafOpties.find((o) => o.waarde === vanaf);
  const callIso = vc && keuze && keuze.week <= vc.week
    ? (() => { const d = plusDagen(vanIso(cd.vanafOpties.find((o) => o.week === vc.week)?.maandag || keuze.maandag), Number(dag)); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })()
    : vc?.callDatum || venster?.callMaandag;
  const moment = (dagen, tijd) => {
    if (!callIso) return "";
    const dt = plusDagen(vanIso(callIso), -dagen);
    return `→ ${DAGNAMEN[maDag(dt)]} ${dt.getDate()} ${MND[dt.getMonth()]} ${/^\d{1,2}:\d{2}$/.test(tijd || "") ? tijd : "--:--"}`;
  };
  const bewaar = async (fn, ok) => {
    setSaving(true);
    try { const r = await fn(); toast.success(ok(r)); onSaved(); }
    catch (e) { if (e?.response?.status === 409) { toast.error(e.response.data.detail); onSaved(); } else err(e, "Opslaan mislukt"); }
    finally { setSaving(false); }
  };
  const saveDag = () => bewaar(() => api.put("/config/call-dag", { dag: Number(dag), vanaf, baseVersion: meta.configVersions?.callDag }),
    () => `Calldag opgeslagen: ${DAGNAMEN[Number(dag)]}`);
  const save = () => bewaar(() => api.put("/config/prep-window", { value: { ...w, anker: "call" }, anker: "call", baseVersion: meta.configVersions?.prepWindow }),
    (r) => `Venster opgeslagen — ${r.data.venster.vensterLabel}`);
  const num = (v) => Math.max(0, Math.min(14, parseInt(v || "0", 10) || 0));
  const lbl = "block text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-1";
  return (
    <div className={card} data-testid="prep-window-editor">
      <h2 className={sectionTitle}>Calldag en aanlevervenster</h2>
      <p className={sectionDesc}>
        Op welke dag de calls zijn, en vanaf wanneer en tot wanneer een locatie de voorbereiding kan aanleveren. Alles in Nederlandse tijd.
        {venster && <> Nu: <span className="font-semibold text-zinc-700">{venster.vensterLabel}</span>, eerstvolgende call {vc?.callLabelLang || venster.callMaandagLabel}.</>}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
        <div>
          <label className={lbl}>Calldag</label>
          <select data-testid="call-dag-select" className={input} value={dag} onChange={(e) => setDag(Number(e.target.value))}>
            {DAGNAMEN.slice(0, 5).map((n, i) => <option key={n} value={i}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Geldt vanaf</label>
          <select data-testid="call-dag-vanaf" className={input} value={vanaf} onChange={(e) => setVanaf(e.target.value)}>
            {cd.vanafOpties.map((o) => <option key={o.waarde} value={o.waarde} label={`${o.waarde === "deze" ? "deze week" : "volgende week"} (call ${kortLabel(plusDagen(vanIso(o.maandag), Number(dag)))})`} />)}
          </select>
        </div>
      </div>
      {cd.historieRegels?.length > 0 && <p data-testid="call-dag-historie" className="text-xs text-zinc-500 mb-2">{cd.historieRegels.join(" ")}</p>}
      <button className={`${btnPrimary} mb-3 md:mb-4`} data-testid="save-call-dag" onClick={saveDag} disabled={saving}><Save className="h-4 w-4" />Calldag opslaan</button>
      <CallGrensEditor meta={meta} onSaved={onSaved} input={input} btnPrimary={btnPrimary} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>Openen — dagen vóór de call</label>
          <div className="flex gap-2">
            <input data-testid="prep-window-open-days" type="number" min={0} max={14} className={`${input} max-w-[90px]`} value={w.openDaysBefore}
              onChange={(e) => setW({ ...w, openDaysBefore: num(e.target.value) })} />
            <input data-testid="prep-window-open-time" className={`${input} max-w-[110px]`} value={w.openTime} placeholder="00:00"
              onChange={(e) => setW({ ...w, openTime: e.target.value })} />
          </div>
          <p data-testid="prep-window-open-moment" className="text-xs text-zinc-500 mt-1">{moment(w.openDaysBefore, w.openTime)}</p>
        </div>
        <div>
          <label className={lbl}>Sluiten — dagen vóór de call (0 = op de calldag zelf)</label>
          <div className="flex gap-2">
            <input data-testid="prep-window-close-days" type="number" min={0} max={14} className={`${input} max-w-[90px]`} value={w.closeDaysBefore}
              onChange={(e) => setW({ ...w, closeDaysBefore: num(e.target.value) })} />
            <input data-testid="prep-window-close-time" className={`${input} max-w-[110px]`} value={w.closeTime} placeholder="09:00"
              onChange={(e) => setW({ ...w, closeTime: e.target.value })} />
          </div>
          <p data-testid="prep-window-close-moment" className="text-xs text-zinc-500 mt-1">{moment(w.closeDaysBefore, w.closeTime)}</p>
        </div>
      </div>
      <button className={`${btnPrimary} mt-4`} data-testid="save-prep-window" onClick={save} disabled={saving}><Save className="h-4 w-4" />{saving ? "Opslaan…" : "Opslaan"}</button>
    </div>
  );
}

function ListEditor({ testid, title, desc, columns, initial, prefix, endpoint, onSaved, newRow, baseVersion, extraPayload }) {
  const [rows, setRows] = useState(initial);
  const [saving, setSaving] = useState(false);
  // Na een herlaad (bv. door een 409-conflict) verandert baseVersion → verse serverdata overnemen.
  useEffect(() => { setRows(initial); }, [baseVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const upd = (i, k, v) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const del = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const add = () => setRows((r) => [...r, { ...newRow }]);
  const save = async () => {
    setSaving(true);
    try { await api.put(endpoint, { items: rows, baseVersion, ...(extraPayload || {}) }); toast.success("Opgeslagen"); onSaved(); }
    catch (e) {
      if (e?.response?.status === 409) { toast.error(e.response.data.detail); onSaved(); }
      else err(e, "Opslaan mislukt");
    } finally { setSaving(false); }
  };
  return (
    <div className={card} data-testid={testid}>
      <h2 className={sectionTitle}>{title}</h2>
      <p className={sectionDesc}>{desc}</p>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`${testid}-row-${i}`}>
            {columns(row, i, upd)}
            <button data-testid={`${testid}-del-${i}`} onClick={() => del(i)} className="mt-0.5 h-9 w-9 shrink-0 rounded-md border border-zinc-300 flex items-center justify-center text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button className={btnGhost} data-testid={`${testid}-add`} onClick={add}><Plus className="h-4 w-4" /> Toevoegen</button>
        <button className={btnPrimary} data-testid={`${testid}-save`} onClick={save} disabled={saving}><Save className="h-4 w-4" />{saving ? "Opslaan…" : "Opslaan"}</button>
      </div>
    </div>
  );
}

function KpiEditor({ meta, onSaved }) {
  return (
    <ListEditor testid="kpi-editor" title="KPI's & denkvragen" desc="De cijfers in de call met hun denkvraag."
      initial={meta.kpis.map((k) => ({ ...k }))} endpoint="/config/kpis" onSaved={onSaved} newRow={{ label: "", vraag: "" }} baseVersion={meta.configVersions?.kpis}
      columns={(row, i, upd) => (<>
        <input className={`${input} max-w-[200px]`} placeholder="Label" value={row.label || ""} onChange={(e) => upd(i, "label", e.target.value)} />
        <input className={input} placeholder="Denkvraag" value={row.vraag || ""} onChange={(e) => upd(i, "vraag", e.target.value)} />
      </>)} />
  );
}

function BezoekChecksEditor({ meta, onSaved }) {
  return (
    <ListEditor testid="bezoek-checks-editor" title="Bezoek-checklist" desc="De punten die je bij een locatiebezoek beoordeelt."
      initial={(meta.bezoekChecks || []).map((p) => ({ ...p }))} endpoint="/config/bezoek-checks" onSaved={onSaved} newRow={{ text: "" }} baseVersion={meta.configVersions?.bezoekChecks}
      columns={(row, i, upd) => (<>
        <span className="mt-2 font-mono text-xs text-zinc-400 w-8 shrink-0">{i + 1}.</span>
        <input className={input} data-testid={`bezoek-checks-editor-text-${i}`} placeholder="Punt" value={row.text || ""} onChange={(e) => upd(i, "text", e.target.value)} />
      </>)} />
  );
}

const freqWaarde = (row) => (row.frequentie === "tweewekelijks" ? `tweewekelijks|${row.pariteit || "even"}` : row.frequentie || "wekelijks");

function PrepEditor({ meta, onSaved }) {
  const opties = meta.frequentieOpties || [];
  const zetFreq = (i, upd, v) => {
    const [f, par] = v.split("|");
    upd(i, "frequentie", f);
    upd(i, "pariteit", par || undefined);
  };
  return (
    <ListEditor testid="prep-editor" title="Checks tijdens de call (p4–p10)" desc="De checks die je tijdens de call afloopt. Per check kies je hoe vaak hij terugkomt."
      initial={meta.prepChecks.map((p) => ({ ...p }))} endpoint="/config/prep-checks" onSaved={onSaved} newRow={{ text: "" }} baseVersion={meta.configVersions?.prepChecks}
      columns={(row, i, upd) => (<>
        <span className="mt-2 font-mono text-xs text-zinc-400 w-10 shrink-0">{row.id || "nw"}</span>
        <div className="flex-1 min-w-0 flex flex-col sm:flex-row gap-2">
          <input className={input} placeholder="Checktekst" value={row.text || ""} onChange={(e) => upd(i, "text", e.target.value)} />
          <label className="sr-only" htmlFor={`prep-freq-${i}`}>Hoe vaak</label>
          <select id={`prep-freq-${i}`} data-testid={`prep-editor-freq-${i}`} aria-label="Hoe vaak" value={freqWaarde(row)} onChange={(e) => zetFreq(i, upd, e.target.value)}
            className={`${input} sm:max-w-[340px] bg-white`}>
            {opties.map((o) => <option key={o.waarde} value={o.waarde}>{o.label}</option>)}
          </select>
        </div>
      </>)} />
  );
}

const PROFILE_LABELS = { om: "OM", bedrijfsleider: "Bedrijfsleider", ceo: "CEO", coo: "COO" };
const ROLE_PROFIELEN = { om: ["om"], bedrijfsleider: ["bedrijfsleider"], oversight: ["ceo", "coo"] };

function GeldtVoor({ row, i, upd, mensen }) {
  const uit = row.nietVoor || [];
  const weg = uit.filter((u) => !mensen.some((m) => m.username === u));
  const zet = (u, aan) => upd(i, "nietVoor", aan ? uit.filter((x) => x !== u) : [...uit, u]);
  return (
    <details data-testid={`task-geldtvoor-${i}`} className="shrink-0 mt-0.5 relative max-w-[150px] sm:max-w-[220px]">
      <summary data-testid={`task-geldtvoor-knop-${i}`} className="list-none cursor-pointer min-h-9 min-w-[44px] px-2.5 py-1.5 rounded-md border border-zinc-300 bg-white flex items-center text-xs font-semibold text-zinc-700 break-words hover:border-zinc-400">
        {uit.length === 0 ? "Geldt voor iedereen" : `Niet voor: ${uit.join(", ")}`}
      </summary>
      <div className="mt-1 min-w-[180px] rounded-[10px] border border-zinc-200 bg-white p-2 space-y-1 shadow-sm">
        {mensen.length === 0 && weg.length === 0 && <p className="text-xs text-zinc-500">Geen actieve gebruikers met dit profiel.</p>}
        {mensen.map((m) => (
          <label key={m.username} className="flex items-center gap-2 min-h-[36px] text-sm text-zinc-800 cursor-pointer">
            <input type="checkbox" data-testid={`task-geldtvoor-${i}-${m.username}`} checked={!uit.includes(m.username)} onChange={(e) => zet(m.username, e.target.checked)} className="h-4 w-4 accent-blue-600" />
            <span>{m.username}{m.locatie ? <span className="text-zinc-400"> · {m.locatie}</span> : null}</span>
          </label>
        ))}
        {weg.map((u) => (
          <label key={u} className="flex items-center gap-2 min-h-[36px] text-sm text-zinc-500 cursor-pointer">
            <input type="checkbox" data-testid={`task-geldtvoor-${i}-${u}`} checked={false} onChange={(e) => zet(u, e.target.checked)} className="h-4 w-4 accent-blue-600" />
            <span>{u} (niet meer actief)</span>
          </label>
        ))}
      </div>
    </details>
  );
}

function TaskEditor({ meta, users = [], onSaved }) {
  const profiles = meta.weekritmeProfiles || ["om"];
  const [profiel, setProfiel] = useState("om");
  const byProfile = meta.weekritmeTasksByProfile || { om: meta.weekritmeTasks || [] };
  const versionKey = profiel === "om" ? "weekritmeTasks" : `weekritmeTasks_${profiel}`;
  const mensen = users.filter((u) => u.profiel === profiel && u.active);
  return (
    <div className="space-y-4" data-testid="weekritme-tasks-editor">
      <div className="flex flex-wrap gap-2" data-testid="weekritme-profile-tabs">
        {profiles.map((p) => (
          <button key={p} type="button" data-testid={`weekritme-profile-${p}`} onClick={() => setProfiel(p)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${profiel === p ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-300 text-zinc-500 hover:border-zinc-400"}`}>
            {PROFILE_LABELS[p] || meta.weekritmeProfielLabels?.[p] || p}{(meta.weekritmeConcept || []).includes(p) && <span data-testid={`weekritme-concept-${p}`} className="ml-1.5 text-[10px] uppercase tracking-wider text-amber-600">concept — pas aan</span>}
          </button>
        ))}
      </div>
      <ListEditor key={profiel} testid="task-editor"
        title={`Weekritme-regels · ${PROFILE_LABELS[profiel] || meta.weekritmeProfielLabels?.[profiel] || profiel}`}
        desc="De dagelijkse taken, disciplines en rooster-items per dag — apart per profiel. CEO en COO starten leeg."
        initial={(byProfile[profiel] || []).map((t) => ({ ...t }))}
        endpoint="/config/weekritme-tasks" extraPayload={{ profiel }} onSaved={onSaved}
        newRow={{ day: "Maandag", text: "", type: "taak" }} baseVersion={meta.configVersions?.[versionKey]}
        columns={(row, i, upd) => (<>
          <select className={`${input} max-w-[140px]`} value={row.day || "Maandag"} onChange={(e) => upd(i, "day", e.target.value)}>{DAYS.map((d) => <option key={d} value={d}>{d}</option>)}</select>
          <input className={input} placeholder="Taaktekst" value={row.text || ""} onChange={(e) => upd(i, "text", e.target.value)} />
          <select className={`${input} max-w-[140px]`} value={row.type || "taak"} onChange={(e) => upd(i, "type", e.target.value)}>{TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}</select>
          <GeldtVoor row={row} i={i} upd={upd} mensen={mensen} />
        </>)} />
    </div>
  );
}

function LocationEditor({ meta, onSaved, setConfirm }) {
  const [locs, setLocs] = useState(meta.locations);
  const [edits, setEdits] = useState({});
  const [newName, setNewName] = useState("");
  const [locOms, setLocOms] = useState(meta.locationOms || {});
  const [savingOms, setSavingOms] = useState(false);
  const [omsVersion, setOmsVersion] = useState(meta.configVersions?.locationOms ?? 0);
  const oms = meta.oms || [];
  const buckets = meta.generalBuckets || [];
  useEffect(() => { setLocs(meta.locations); }, [meta.locations]);
  useEffect(() => { setLocOms(meta.locationOms || {}); }, [meta.locationOms]);
  useEffect(() => { setOmsVersion(meta.configVersions?.locationOms ?? 0); }, [meta.configVersions?.locationOms]);

  const add = async () => {
    if (!newName.trim()) return;
    try { const r = await api.post("/config/locations", { name: newName.trim() }); setLocs(r.data); setNewName(""); toast.success("Locatie toegevoegd"); onSaved(); }
    catch (e) { err(e, "Toevoegen mislukt"); }
  };
  const rename = async (old) => {
    const nn = (edits[old] ?? old).trim();
    if (nn === old || !nn) return;
    try { const r = await api.put("/config/locations", { oldName: old, newName: nn }); setLocs(r.data); setEdits({}); toast.success("Locatie hernoemd"); onSaved(); }
    catch (e) { err(e, "Hernoemen mislukt"); }
  };
  const doDelete = async (name) => {
    try { const r = await api.delete(`/config/locations?name=${encodeURIComponent(name)}`); setLocs(r.data); toast.success("Locatie verwijderd"); onSaved(); }
    catch (e) { err(e, "Verwijderen mislukt"); }
  };
  const toggleOm = async (loc, om) => {
    const current = locOms[loc] || [];
    const next = current.includes(om) ? current.filter((x) => x !== om) : [...current, om];
    const mapping = { ...locOms };
    if (next.length) mapping[loc] = next; else delete mapping[loc];
    setLocOms(mapping);
    setSavingOms(true);
    try { const r = await api.put("/config/location-oms", { value: mapping, baseVersion: omsVersion }); setLocOms(r.data); setOmsVersion((v) => v + 1); onSaved(); }
    catch (e) {
      if (e?.response?.status === 409) { toast.error(e.response.data.detail); onSaved(); }
      else err(e, "Toewijzing opslaan mislukt");
      setLocOms(meta.locationOms || {});
    }
    finally { setSavingOms(false); }
  };
  // A5: verwijzingen naar namen die geen actieve OM meer zijn (gedeactiveerd/gedegradeerd/verwijderd).
  const ghostsOf = (key) => (locOms[key] || []).filter((o) => !oms.includes(o));
  const removeGhost = (loc, ghost) => {
    // Let op: opslaan stuurt de hele mapping → de backend stript ALLE onbekende namen tegelijk.
    const allGhosts = Object.entries(locOms).flatMap(([k, arr]) => (arr || []).filter((o) => !oms.includes(o)).map((o) => `${k}: ${o}`));
    const many = allGhosts.length > 1;
    setConfirm({
      title: "Onbekende toewijzing verwijderen?",
      desc: many
        ? `Opslaan ruimt in één keer alle ${allGhosts.length} onbekende toewijzingen op: ${allGhosts.join(", ")}.`
        : `"${ghost}" wordt verwijderd van ${loc}.`,
      confirmLabel: "Verwijderen",
      onConfirm: async () => { await toggleOm(loc, ghost); toast.success(`${allGhosts.length} onbekende toewijzing(en) opgeruimd`); },
    });
  };

  return (
    <div className={card} data-testid="location-editor">
      <h2 className={sectionTitle}>Locatielijst</h2>
      <p className={sectionDesc}>Voedt de locatie-keuzes in locatie-calls én afspraken. Een locatie in gebruik kan niet verwijderd worden. Wijs OM's toe om locaties te filteren — niet-toegewezen locaties blijven voor iedereen zichtbaar.</p>
      <div className="flex items-center gap-2 mb-4">
        <input className={`${input} max-w-xs`} data-testid="new-location-name" placeholder="Nieuwe locatie" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <button className={btnPrimary} data-testid="add-location" onClick={add}><Plus className="h-4 w-4" /> Toevoegen</button>
      </div>
      <div className="space-y-2">
        {locs.map((l) => (
          <div key={l} className="rounded-md border border-zinc-200 p-2.5 space-y-2" data-testid={`location-row-${l}`}>
            <div className="flex items-center gap-2">
              <input className={input} value={edits[l] ?? l} onChange={(e) => setEdits({ ...edits, [l]: e.target.value })} />
              <button className={btnGhost} data-testid={`rename-location-${l}`} onClick={() => rename(l)} disabled={(edits[l] ?? l) === l}><Pencil className="h-4 w-4" /> Hernoemen</button>
              <button data-testid={`delete-location-${l}`} onClick={() => setConfirm({ title: `Locatie "${l}" verwijderen?`, desc: "Dit kan niet als de locatie nog in gebruik is.", confirmLabel: "Verwijderen", onConfirm: () => doDelete(l) })}
                className="h-9 w-9 shrink-0 rounded-md border border-zinc-300 flex items-center justify-center text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
            {(oms.length > 0 || ghostsOf(l).length > 0) && (
              <div className="flex flex-wrap items-center gap-1.5 pl-0.5" data-testid={`location-oms-${l}`}>
                <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-400 mr-1">OM's:</span>
                {oms.map((om) => {
                  const on = (locOms[l] || []).includes(om);
                  return (
                    <button key={om} type="button" disabled={savingOms} data-testid={`location-om-toggle-${l}-${om}`} onClick={() => toggleOm(l, om)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize border transition-colors ${on ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-zinc-300 text-zinc-500 hover:border-zinc-400"} ${savingOms ? "opacity-60" : ""}`}>
                      {om}
                    </button>
                  );
                })}
                {(!locOms[l] || locOms[l].length === 0) && <span className="text-[11px] text-zinc-400 italic">iedereen</span>}
                {ghostsOf(l).map((g) => (
                  <button key={g} type="button" disabled={savingOms} data-testid={`location-om-ghost-${l}-${g}`} onClick={() => removeGhost(l, g)} title="Onbekend/uit dienst — klik om te verwijderen"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-dashed border-amber-300 bg-amber-50 text-amber-700 line-through hover:bg-amber-100 transition-colors">
                    {g} <span className="not-italic no-underline text-[10px] text-amber-800 uppercase tracking-wider">onbekend/uit dienst</span> <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {buckets.map((bkt) => (
          <div key={bkt} className="rounded-md border border-blue-200 bg-blue-50/40 p-2.5 space-y-2" data-testid={`bucket-row-${bkt}`}>
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-zinc-800">{bkt}</span>
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">Systeem · algemeen</span>
            </div>
            <p className="text-[11px] text-zinc-500">Overkoepelende afspraak-categorie — geen wekelijkse locatie-call. Toewijsbaar aan OM's.</p>
            {(oms.length > 0 || ghostsOf(bkt).length > 0) && (
              <div className="flex flex-wrap items-center gap-1.5 pl-0.5" data-testid={`location-oms-${bkt}`}>
                <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-400 mr-1">OM's:</span>
                {oms.map((om) => {
                  const on = (locOms[bkt] || []).includes(om);
                  return (
                    <button key={om} type="button" disabled={savingOms} data-testid={`location-om-toggle-${bkt}-${om}`} onClick={() => toggleOm(bkt, om)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize border transition-colors ${on ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-zinc-300 text-zinc-500 hover:border-zinc-400"} ${savingOms ? "opacity-60" : ""}`}>
                      {om}
                    </button>
                  );
                })}
                {(!locOms[bkt] || locOms[bkt].length === 0) && <span className="text-[11px] text-zinc-400 italic">iedereen</span>}
                {ghostsOf(bkt).map((g) => (
                  <button key={g} type="button" disabled={savingOms} data-testid={`location-om-ghost-${bkt}-${g}`} onClick={() => removeGhost(bkt, g)} title="Onbekend/uit dienst — klik om te verwijderen"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border border-dashed border-amber-300 bg-amber-50 text-amber-700 line-through hover:bg-amber-100 transition-colors">
                    {g} <span className="not-italic no-underline text-[10px] text-amber-800 uppercase tracking-wider">onbekend/uit dienst</span> <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function UserEditor({ users, me, meta, onSaved, onMetaSaved, setConfirm }) {
  const [nu, setNu] = useState({ username: "", role: "om", password: "", canDoOm: false, locatie: "", afdelingen: [] });
  const [transferFrom, setTransferFrom] = useState(null); // username string
  const [transferTo, setTransferTo] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [deactivateUser, setDeactivateUser] = useState(null); // username string
  const [deactivateTo, setDeactivateTo] = useState("");
  const [deactivateBusy, setDeactivateBusy] = useState(false);

  const locOms = meta.locationOms || {};
  const activeOms = meta.oms || [];
  const transferLocs = transferFrom
    ? Object.entries(locOms).filter(([, oms]) => (oms || []).includes(transferFrom)).map(([loc]) => loc)
    : [];
  const transferTargets = transferFrom ? activeOms.filter((o) => o !== transferFrom) : [];

  const locsOf = (username) => Object.entries(locOms).filter(([, oms]) => (oms || []).includes(username)).map(([loc]) => loc);
  const deactivateLocs = deactivateUser ? locsOf(deactivateUser) : [];
  const deactivateTargets = deactivateUser ? activeOms.filter((o) => o !== deactivateUser) : [];

  const openTransfer = (username) => { setTransferFrom(username); setTransferTo(""); };
  const doTransfer = async () => {
    if (!transferFrom || !transferTo) return;
    setTransferBusy(true);
    try {
      const r = await api.post("/config/location-oms/transfer", {
        fromOm: transferFrom, toOm: transferTo, baseVersion: meta.configVersions?.locationOms,
      });
      toast.success(`${r.data.moved} ${r.data.moved === 1 ? "locatie" : "locaties"} overgedragen naar ${transferTo}`);
      setTransferFrom(null); setTransferTo("");
      onMetaSaved && onMetaSaved();
    } catch (e) {
      err(e, "Overdragen mislukt");
      if (e?.response?.status === 409) { setTransferFrom(null); onMetaSaved && onMetaSaved(); }
    } finally { setTransferBusy(false); }
  };

  const doDeactivateWithTransfer = async () => {
    if (!deactivateUser) return;
    const fromOm = deactivateUser, toOm = deactivateTo;
    setDeactivateBusy(true);
    try {
      // A3: draag EERST over (anders ruimt de deactivering-cascade de locaties al op), deactiveer daarna.
      if (toOm) {
        try {
          const r = await api.post("/config/location-oms/transfer", {
            fromOm, toOm, baseVersion: meta.configVersions?.locationOms,
          });
          toast.success(`${r.data.moved} ${r.data.moved === 1 ? "locatie" : "locaties"} overgedragen naar ${toOm}`);
        } catch (e2) {
          // Overdracht mislukt -> NIET deactiveren; laat de gebruiker in een verklaarde staat achter.
          if (e2?.response?.status === 409) toast.error(e2.response.data.detail);
          else err(e2, "Overdragen mislukt — gebruiker is niet gedeactiveerd");
          onMetaSaved && onMetaSaved();
          setDeactivateBusy(false);
          return;
        }
      }
      const res = await api.patch(`/users/${fromOm}/active`, { active: false });
      toast.success("Gebruiker gedeactiveerd");
      const freed = res.data?.locatiesZonderOm || [];
      if (freed.length) toast.warning(`${freed.length} locatie(s) hebben nu geen OM meer — wijs een opvolger toe.`);
      setDeactivateUser(null); setDeactivateTo("");
      onSaved(); onMetaSaved && onMetaSaved();
    } catch (e) {
      err(e, "Deactiveren mislukt");
      onMetaSaved && onMetaSaved();
    } finally { setDeactivateBusy(false); }
  };

  const create = async () => {
    if (!nu.username.trim()) return;
    if (nu.role === "bedrijfsleider" && !nu.locatie) { toast.error("Kies een locatie voor de bedrijfsleider"); return; }
    if (nu.role === "afdeling" && !nu.afdelingen.length) { toast.error("Kies minstens één afdeling"); return; }
    try {
      const r = await api.post("/users", { username: nu.username.trim(), role: nu.role, password: nu.password || null, canDoOm: nu.role === "oversight" ? nu.canDoOm : false, locatie: nu.role === "bedrijfsleider" ? nu.locatie : null, ...(nu.role === "afdeling" ? { afdelingen: nu.afdelingen } : {}) });
      if (r.data.tempPassword) setTijdelijk({ username: r.data.username, wachtwoord: r.data.tempPassword });
      else toast.success("Gebruiker aangemaakt");
      setNu({ username: "", role: "om", password: "", canDoOm: false, locatie: "", afdelingen: [] }); onSaved(); onMetaSaved && onMetaSaved();
    } catch (e) { err(e, "Aanmaken mislukt"); }
  };
  // Locaties beschikbaar voor een NIEUWE bedrijfsleider: geen buckets, en niet al door een actieve bl bezet.
  const blBuckets = meta.generalBuckets || [];
  const takenBlLocs = new Set(users.filter((u) => u.role === "bedrijfsleider" && u.active !== false && u.locatie).map((u) => u.locatie));
  const blLocations = (meta.locations || []).filter((l) => !blBuckets.includes(l));
  const freedToast = (res) => {
    const freed = res?.data?.locatiesZonderOm || [];
    if (freed.length) toast.warning(`${freed.length} locatie(s) hebben nu geen OM meer — wijs een opvolger toe.`);
    return freed;
  };
  const changeRole = async (username, role) => {
    try { const r = await api.patch(`/users/${username}/role`, { role }); toast.success("Rol bijgewerkt"); freedToast(r); onSaved(); onMetaSaved && onMetaSaved(); }
    catch (e) { err(e, "Rol wijzigen mislukt"); }
  };
  const changeProfiel = async (username, profiel) => {
    try { await api.patch(`/users/${username}/profiel`, { profiel: profiel || null }); toast.success("Weekritme-profiel bijgewerkt"); onSaved(); }
    catch (e) { err(e, "Profiel wijzigen mislukt"); }
  };
  const toggleCanDoOm = async (username, canDoOm) => {
    try { const r = await api.patch(`/users/${username}/om-capability`, { canDoOm }); toast.success(canDoOm ? "OM-taken aangezet" : "OM-taken uitgezet"); freedToast(r); onSaved(); onMetaSaved && onMetaSaved(); }
    catch (e) { err(e, "Bijwerken mislukt"); }
  };
  const setActive = async (username, active) => {
    try {
      const res = await api.patch(`/users/${username}/active`, { active });
      toast.success(active ? "Gebruiker geheractiveerd" : "Gebruiker gedeactiveerd");
      const freed = res.data?.locatiesZonderOm || [];
      if (!active && freed.length) toast.warning(`${freed.length} locatie(s) hebben nu geen OM meer — wijs een opvolger toe.`);
      onSaved(); onMetaSaved && onMetaSaved();
    }
    catch (e) { err(e, "Bijwerken mislukt"); }
  };
  const [tijdelijk, setTijdelijk] = useState(null);
  const resetPwd = async (username) => {
    try { const r = await api.post(`/users/${username}/reset-password`); setTijdelijk({ username, wachtwoord: r.data.tempPassword }); onSaved(); }
    catch (e) { err(e, "Reset mislukt"); }
  };
  const doDelete = async (username) => {
    try { const r = await api.delete(`/users/${username}`); toast.success("Gebruiker verwijderd"); freedToast(r); onSaved(); onMetaSaved && onMetaSaved(); }
    catch (e) { err(e, "Verwijderen mislukt"); }
  };

  return (
    <div className={card} data-testid="user-editor">
      <TijdelijkWachtwoordDialog info={tijdelijk} onClose={() => setTijdelijk(null)} />
      <h2 className={sectionTitle}>Gebruikers & rollen</h2>
      <p className={sectionDesc}>Beheer accounts. Deactiveren behoudt alle historie (niets wordt verwijderd). De laatste actieve oversight kan niet gedegradeerd, gedeactiveerd of verwijderd worden. "Mag ook OM-taken" laat een oversight bijspringen als OM.</p>

      <div className="flex flex-wrap items-center gap-2 mb-3 md:mb-4 p-3 rounded-md bg-zinc-50 border border-zinc-200">
        <input className={`${input} max-w-[160px]`} data-testid="new-user-username" placeholder="Gebruikersnaam" value={nu.username} onChange={(e) => setNu({ ...nu, username: e.target.value })} />
        <select className={`${input} max-w-[140px]`} data-testid="new-user-role" value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}>
          <option value="om">OM</option><option value="oversight">Oversight</option><option value="bedrijfsleider">Bedrijfsleider</option>
          {me.role === "oversight" && <option value="afdeling">Afdeling</option>}
        </select>
        {nu.role === "afdeling" && (
          <div data-testid="new-user-afdelingen" className="flex flex-wrap gap-x-3 gap-y-1">
            {(meta.afdelingen || []).filter((a) => a.actief !== false).map((a) => (
              <label key={a.id} className="flex items-center gap-1.5 text-sm text-zinc-700 select-none">
                <input type="checkbox" data-testid={`new-user-afdeling-${a.id}`} className="h-4 w-4 rounded border-zinc-300"
                  checked={nu.afdelingen.includes(a.id)}
                  onChange={(e) => setNu({ ...nu, afdelingen: e.target.checked ? [...nu.afdelingen, a.id] : nu.afdelingen.filter((x) => x !== a.id) })} />
                {a.naam}
              </label>
            ))}
          </div>
        )}
        {nu.role === "bedrijfsleider" && (
          <select className={`${input} max-w-[200px]`} data-testid="new-user-locatie" value={nu.locatie} onChange={(e) => setNu({ ...nu, locatie: e.target.value })}>
            <option value="">Kies locatie…</option>
            {blLocations.map((l) => (
              <option key={l} value={l} disabled={takenBlLocs.has(l)}>{l}{takenBlLocs.has(l) ? " — al bezet" : ""}</option>
            ))}
          </select>
        )}
        <input className={`${input} max-w-[200px]`} data-testid="new-user-password" placeholder="Wachtwoord (leeg = tijdelijk)" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} />
        {nu.role === "oversight" && (
          <label className="flex items-center gap-1.5 text-sm text-zinc-700 select-none">
            <input type="checkbox" data-testid="new-user-candoom" checked={nu.canDoOm} onChange={(e) => setNu({ ...nu, canDoOm: e.target.checked })} className="h-4 w-4 rounded border-zinc-300" />
            mag ook OM-taken
          </label>
        )}
        <button className={btnPrimary} data-testid="add-user" onClick={create}><Plus className="h-4 w-4" /> Toevoegen</button>
      </div>

      <div className="space-y-2">
        {users.map((u) => {
          const inactive = u.active === false;
          const isSelf = u.username === me.username;
          return (
          <div key={u.username} className={`flex flex-wrap items-center gap-2 p-3 rounded-md border ${inactive ? "border-zinc-200 bg-zinc-100/70 opacity-80" : "border-zinc-200"}`} data-testid={`user-row-${u.username}`}>
            <div className="flex items-center gap-2 min-w-[160px] flex-1">
              {u.role === "oversight" ? <ShieldCheck className="h-4 w-4 text-blue-600" /> : <User className="h-4 w-4 text-zinc-400" />}
              <span className="font-semibold text-zinc-900 capitalize">{u.username}</span>
              {isSelf && <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">(jij)</span>}
              {inactive && <span data-testid={`user-inactive-${u.username}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-300 text-zinc-700 font-semibold">gedeactiveerd</span>}
              {u.role === "oversight" && u.canDoOm && !inactive && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-semibold">+ OM-taken</span>}
              {u.usingDefault && <span data-testid={`tijdelijk-ww-badge-${u.username}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">tijdelijk wachtwoord</span>}
            </div>
            {u.role === "afdeling" ? (
              me.role === "oversight" ? <AfdelingenWijzig u={u} meta={meta} onSaved={onSaved} /> : (
              <span data-testid={`user-afdelingen-${u.username}`} className="text-xs text-zinc-600">
                Afdeling: {(meta.afdelingen || []).filter((x) => (u.afdelingen || []).includes(x.id)).map((x) => x.naam).join(", ")}
              </span>)
            ) : u.role === "bedrijfsleider" ? (
              <span data-testid={`role-label-${u.username}`} className="text-xs font-semibold text-zinc-600">Bedrijfsleider</span>
            ) : (
            <select className={`${input} max-w-[140px]`} data-testid={`role-${u.username}`} value={u.role} onChange={(e) => changeRole(u.username, e.target.value)} disabled={inactive}>
              <option value="om">OM</option><option value="oversight">Oversight</option>
            </select>
            )}
            {me.role === "oversight" && u.role !== "afdeling" && (
              <label className="flex items-center gap-1.5 text-xs text-zinc-500 select-none whitespace-nowrap">
                <span className="uppercase tracking-wider font-semibold">Profiel</span>
                <select className={`${input} max-w-[150px]`} data-testid={`profiel-${u.username}`} value={u.profiel || ""} disabled={inactive} onChange={(e) => changeProfiel(u.username, e.target.value)}>
                  <option value="">— geen —</option>
                  {(ROLE_PROFIELEN[u.role] || []).map((p) => (
                    <option key={p} value={p}>{PROFILE_LABELS[p] || p}</option>
                  ))}
                </select>
              </label>
            )}
            {u.role === "oversight" && (
              <label className="flex items-center gap-1.5 text-xs text-zinc-700 select-none whitespace-nowrap">
                <input type="checkbox" data-testid={`candoom-${u.username}`} checked={!!u.canDoOm} disabled={inactive} onChange={(e) => toggleCanDoOm(u.username, e.target.checked)} className="h-4 w-4 rounded border-zinc-300" />
                OM-taken
              </label>
            )}
            <button className={btnGhost} data-testid={`reset-pwd-${u.username}`} onClick={() => resetPwd(u.username)}><KeyRound className="h-4 w-4" /> Reset wachtwoord</button>
            {(u.role === "om" || u.canDoOm) && (
              <button className={btnGhost} data-testid={`transfer-locaties-${u.username}`} onClick={() => openTransfer(u.username)}>
                <ArrowRightLeft className="h-4 w-4" /> Locaties overdragen
              </button>
            )}
            <button data-testid={`toggle-active-${u.username}`} disabled={isSelf}
              onClick={() => {
                if (inactive) { setActive(u.username, true); return; }
                const omIdentity = u.role === "om" || u.canDoOm;
                if (omIdentity && locsOf(u.username).length > 0) { setDeactivateUser(u.username); setDeactivateTo(""); return; }
                setConfirm({ title: `Gebruiker "${u.username}" deactiveren?`, desc: "De gebruiker kan niet meer inloggen. Alle historie (calls, weekritme, afspraken) blijft behouden. Locatietoewijzingen worden automatisch opgeruimd. Heractiveren herstelt die toewijzingen niet.", confirmLabel: "Deactiveren", onConfirm: () => setActive(u.username, false) });
              }}
              className={`px-3 py-2 rounded-md border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${inactive ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}>
              {inactive ? "Heractiveren" : "Deactiveren"}
            </button>
            <button data-testid={`delete-user-${u.username}`} disabled={isSelf}
              onClick={() => setConfirm({ title: `Gebruiker "${u.username}" verwijderen?`, desc: "Tip: 'Deactiveren' behoudt de historie. Verwijderen kan niet ongedaan gemaakt worden.", confirmLabel: "Verwijderen", onConfirm: () => doDelete(u.username) })}
              className="h-9 w-9 shrink-0 rounded-md border border-zinc-300 flex items-center justify-center text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 className="h-4 w-4" /></button>
            {u.role === "bedrijfsleider" && <ContactEditor user={u} onSaved={onSaved} />}
          </div>
          );
        })}
      </div>

      <AlertDialog open={!!transferFrom} onOpenChange={(o) => { if (!o) setTransferFrom(null); }}>
        <AlertDialogContent data-testid="transfer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Locaties overdragen van "{transferFrom}"</AlertDialogTitle>
            <AlertDialogDescription>
              Alle locatietoewijzingen van <span className="font-semibold">{transferFrom}</span> worden overgezet naar de gekozen OM. Andere OM's op die locaties blijven behouden. Alleen de toewijzing verandert — historie (calls, afspraken) blijft ongemoeid.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {transferLocs.length === 0 ? (
            <div className="text-sm text-zinc-500 py-2" data-testid="transfer-empty">
              Geen locaties toegewezen aan <span className="font-semibold">{transferFrom}</span>. Er is niets over te dragen.
            </div>
          ) : (
            <div className="space-y-3 py-1">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
                  {transferLocs.length} {transferLocs.length === 1 ? "locatie verhuist" : "locaties verhuizen"}
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="transfer-preview">
                  {transferLocs.map((l) => (
                    <span key={l} className="text-xs px-2 py-1 rounded-md bg-zinc-100 border border-zinc-200 text-zinc-700">{l}</span>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5 block">Overdragen aan</label>
                <select className={input} data-testid="transfer-target" value={transferTo} onChange={(e) => setTransferTo(e.target.value)}>
                  <option value="">— Kies een actieve OM —</option>
                  {transferTargets.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                {transferTargets.length === 0 && <p className="text-xs text-amber-600 mt-1">Geen andere actieve OM beschikbaar.</p>}
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="transfer-cancel">Annuleren</AlertDialogCancel>
            <button
              data-testid="transfer-confirm"
              disabled={transferLocs.length === 0 || !transferTo || transferBusy}
              onClick={doTransfer}
              className={btnPrimary}>
              {transferBusy ? "Bezig…" : `Overdragen${transferLocs.length ? ` (${transferLocs.length})` : ""}`}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deactivateUser} onOpenChange={(o) => { if (!o) setDeactivateUser(null); }}>
        <AlertDialogContent data-testid="deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Gebruiker "{deactivateUser}" deactiveren?</AlertDialogTitle>
            <AlertDialogDescription>
              De gebruiker kan niet meer inloggen. Alle historie (calls, weekritme, afspraken) blijft behouden en je kunt later heractiveren. Heractiveren herstelt de eerdere locatietoewijzingen niet.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-1">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1.5">
                {deactivateLocs.length} toegewezen {deactivateLocs.length === 1 ? "locatie" : "locaties"}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="deactivate-preview">
                {deactivateLocs.map((l) => (
                  <span key={l} className="text-xs px-2 py-1 rounded-md bg-white border border-amber-200 text-amber-800">{l}</span>
                ))}
              </div>
              <p className="text-xs text-amber-700 mt-2">Zonder overdracht worden deze toewijzingen opgeruimd en raken de locaties zonder actieve OM. Kies optioneel een opvolger.</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5 block">Locaties overdragen aan (optioneel)</label>
              <select className={input} data-testid="deactivate-transfer-target" value={deactivateTo} onChange={(e) => setDeactivateTo(e.target.value)}>
                <option value="">— Niet overdragen —</option>
                {deactivateTargets.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">Annuleren</AlertDialogCancel>
            <button
              data-testid="deactivate-confirm"
              disabled={deactivateBusy}
              onClick={doDeactivateWithTransfer}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-60">
              {deactivateBusy ? "Bezig…" : (deactivateTo ? "Deactiveren + overdragen" : "Deactiveren")}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const CATEGORIES = [
  { v: "", l: "Alle categorieën" },
  { v: "auth", l: "Auth" },
  { v: "locatie_call", l: "Locatie-call" },
  { v: "locatie_log", l: "Wat speelt er" },
  { v: "weekritme", l: "Weekritme" },
  { v: "afspraak", l: "Afspraak" },
  { v: "config", l: "Instellingen" },
  { v: "gebruikers", l: "Gebruikers" },
];

const CAT_BADGE = {
  auth: "bg-blue-100 text-blue-700",
  locatie_call: "bg-teal-100 text-teal-700",
  locatie_log: "bg-lime-100 text-lime-800",
  weekritme: "bg-emerald-100 text-emerald-700",
  afspraak: "bg-amber-100 text-amber-800",
  config: "bg-zinc-200 text-zinc-700",
  gebruikers: "bg-violet-100 text-violet-700",
};

const PAGE = 25;

function AuditLog() {
  const [filters, setFilters] = useState({ username: "", category: "", date_from: "", date_to: "" });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [usernames, setUsernames] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPage = async (skip, replace) => {
    setLoading(true);
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    p.append("limit", PAGE); p.append("skip", skip);
    try {
      const r = await api.get(`/audit-log?${p.toString()}`);
      setTotal(r.data.total);
      setUsernames(r.data.usernames || []);
      setItems((prev) => replace ? r.data.items : [...prev, ...r.data.items]);
    } catch (e) { err(e, "Logboek laden mislukt"); } finally { setLoading(false); }
  };

  useEffect(() => { fetchPage(0, true); /* eslint-disable-next-line */ }, [filters]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

  return (
    <div className={card} data-testid="audit-log">
      <div className="flex items-center gap-2 mb-1">
        <ScrollText className="h-5 w-5 text-zinc-700" />
        <h2 className={sectionTitle + " mb-0"}>Logboek</h2>
      </div>
      <p className={sectionDesc}>Wie deed wat, wanneer. Nieuwste eerst. Server-side bijgehouden.</p>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Gebruiker</label>
          <select className={`${input} max-w-[160px]`} data-testid="audit-filter-user" value={filters.username} onChange={(e) => setF("username", e.target.value)}>
            <option value="">Alle gebruikers</option>
            {usernames.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Categorie</label>
          <select className={`${input} max-w-[170px]`} data-testid="audit-filter-category" value={filters.category} onChange={(e) => setF("category", e.target.value)}>
            {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Vanaf</label>
          <input type="date" className={`${input} max-w-[150px]`} data-testid="audit-filter-from" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Tot</label>
          <input type="date" className={`${input} max-w-[150px]`} data-testid="audit-filter-to" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} />
        </div>
        <button className={btnGhost} data-testid="audit-reset" onClick={() => setFilters({ username: "", category: "", date_from: "", date_to: "" })}>Wissen</button>
        <span className="text-xs text-zinc-400 font-mono ml-auto" data-testid="audit-total">{total} regel(s)</span>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-10 text-sm text-zinc-400">Geen logregels gevonden.</div>
      ) : (
        <div className="divide-y divide-zinc-200 border border-zinc-200 rounded-md">
          {items.map((i) => (
            <div key={i.id} className="flex items-start gap-3 px-3 py-2.5" data-testid="audit-row">
              <span className="font-mono text-[11px] text-zinc-400 whitespace-nowrap pt-0.5 w-[110px] shrink-0">{fmt(i.timestamp)}</span>
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${CAT_BADGE[i.category] || "bg-zinc-200 text-zinc-700"}`}>{i.category}</span>
              <span className="text-sm text-zinc-700 flex-1">{i.description}</span>
              <span className="text-xs text-zinc-500 shrink-0 capitalize">{i.username || "—"}{i.role ? ` · ${({ om: "OM", oversight: "oversight", bedrijfsleider: "bedrijfsleider", afdeling: "afdeling" })[i.role] || i.role}` : ""}</span>
            </div>
          ))}
        </div>
      )}

      {items.length < total && (
        <button className={`${btnGhost} mt-4`} data-testid="audit-load-more" onClick={() => fetchPage(items.length, false)} disabled={loading}>
          {loading ? "Laden…" : "Meer laden"}
        </button>
      )}
    </div>
  );
}

const PERIODS = [
  { v: "today", l: "Vandaag" },
  { v: "this_week", l: "Deze week" },
  { v: "last_week", l: "Vorige week" },
  { v: "all", l: "Alles" },
];
const SORTS = [
  { v: "lastLogin", l: "Laatste login" },
  { v: "workCount", l: "Aantal acties" },
  { v: "inactive", l: "Inactief eerst" },
];
const WORK_LABELS = { locatie_call: "Calls", locatie_log: "Wat speelt er", afspraak: "Afspraken", weekritme: "Weekritme", config: "Instellingen", gebruikers: "Gebruikers" };

function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "zojuist";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min geleden`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} uur geleden`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} dag${d > 1 ? "en" : ""} geleden`;
  const mo = Math.floor(d / 30);
  return `${mo} maand${mo > 1 ? "en" : ""} geleden`;
}

function ActivityOverview() {
  const [period, setPeriod] = useState("today");
  const [sort, setSort] = useState("lastLogin");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/activity?period=${period}`)
      .then((r) => setRows(r.data.rows || []))
      .catch((e) => err(e, "Activiteit laden mislukt"))
      .finally(() => setLoading(false));
  }, [period]);

  const sorted = [...rows].sort((a, b) => {
    if (sort === "workCount") return b.workCount - a.workCount;
    if (sort === "inactive") return (b.inactive ? 1 : 0) - (a.inactive ? 1 : 0) || (b.loginCount - a.loginCount);
    return (b.lastLogin || "").localeCompare(a.lastLogin || "");
  });

  const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }) : "—"; } catch { return iso; } };
  const inactiveCount = rows.filter((r) => r.inactive).length;

  return (
    <div className={card} data-testid="activity-overview">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="h-5 w-5 text-zinc-700" />
        <h2 className={sectionTitle + " mb-0"}>Activiteit per gebruiker</h2>
      </div>
      <p className={sectionDesc}>Wie logt in, wie doet écht iets, en wie logt alleen in zonder iets te doen. Afgeleid uit het logboek.</p>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Periode</label>
          <select className={`${input} max-w-[150px]`} data-testid="activity-period" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIODS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Sorteren</label>
          <select className={`${input} max-w-[160px]`} data-testid="activity-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </div>
        {inactiveCount > 0 && (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="activity-inactive-count">
            <AlertTriangle className="h-3.5 w-3.5" />{inactiveCount} ingelogd zonder werk
          </span>
        )}
        <span className="text-xs text-zinc-400 font-mono ml-auto" data-testid="activity-total">{rows.length} gebruiker(s)</span>
      </div>

      {loading ? (
        <div className="text-center py-10 text-sm text-zinc-400">Laden…</div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-10 text-sm text-zinc-400">Geen gebruikers gevonden.</div>
      ) : (
        <div className="space-y-2">
          {sorted.map((r) => (
            <div key={r.username}
              data-testid={`activity-row-${r.username}`}
              className={`flex flex-wrap items-center gap-x-4 gap-y-2 p-3 rounded-md border ${r.inactive ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white"}`}>
              <div className="flex items-center gap-2 min-w-[150px] flex-1">
                {r.role === "oversight" ? <ShieldCheck className="h-4 w-4 text-blue-600" /> : <User className="h-4 w-4 text-zinc-400" />}
                <span className="font-semibold text-zinc-900 capitalize">{r.username}</span>
                {r.inactive && (
                  <span data-testid={`activity-inactive-${r.username}`} className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">
                    <AlertTriangle className="h-3 w-3" /> ingelogd · niets gedaan
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500">
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Laatste login</div>
                <div data-testid={`activity-lastlogin-${r.username}`}>{fmt(r.lastLogin)}{timeAgo(r.lastLogin) ? <span className="text-zinc-400"> · {timeAgo(r.lastLogin)}</span> : ""}</div>
              </div>
              <div className="text-xs text-zinc-500">
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Logins</div>
                <div className="font-mono text-sm" data-testid={`activity-logins-${r.username}`}>{r.loginCount}</div>
              </div>
              <div className="text-xs text-zinc-500">
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Werk-acties</div>
                <div className={`font-mono text-sm ${r.workCount === 0 ? "text-amber-700 font-bold" : "text-emerald-700 font-bold"}`} data-testid={`activity-work-${r.username}`}>{r.workCount}</div>
              </div>
              <div className="text-xs text-zinc-500">
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Laatste werk</div>
                <div data-testid={`activity-lastwork-${r.username}`}>{r.lastWork ? <>{fmt(r.lastWork)}<span className="text-zinc-400"> · {timeAgo(r.lastWork)}</span></> : "—"}</div>
              </div>
              <div className="text-xs text-zinc-500">
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Mislukte logins</div>
                <div className={`font-mono text-sm ${r.failedCount > 0 ? "text-rose-600 font-bold" : "text-zinc-400"}`} data-testid={`activity-failed-${r.username}`}>{r.failedCount}</div>
              </div>
              {r.workCount > 0 && (
                <div className="flex flex-wrap gap-1.5 w-full sm:w-auto">
                  {Object.entries(r.workBreakdown || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                    <span key={k} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">{WORK_LABELS[k] || k}: {n}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Minimal Dutch markdown renderer: ## headings, - bullets, **bold**.
function renderMarkdown(md) {
  const lines = (md || "").split("\n");
  const blocks = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      blocks.push(<ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-2">{list}</ul>);
      list = [];
    }
  };
  const inline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={i} className="font-semibold text-zinc-900">{p.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{p}</React.Fragment>
    );
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith("## ")) {
      flush(idx);
      blocks.push(<h3 key={idx} className="font-heading font-bold text-zinc-900 text-base mt-4 mb-1">{inline(line.slice(3))}</h3>);
    } else if (line.startsWith("# ")) {
      flush(idx);
      blocks.push(<h2 key={idx} className="font-heading font-black text-zinc-900 text-lg mt-4 mb-1">{inline(line.slice(2))}</h2>);
    } else if (/^[-*]\s+/.test(line.trimStart())) {
      list.push(<li key={idx} className="text-sm text-zinc-700">{inline(line.trimStart().replace(/^[-*]\s+/, ""))}</li>);
    } else if (line.trim() === "") {
      flush(idx);
    } else {
      flush(idx);
      blocks.push(<p key={idx} className="text-sm text-zinc-700 my-1">{inline(line)}</p>);
    }
  });
  flush("end");
  return blocks;
}

const AI_LEVELS = [
  { v: "overall", l: "Overall (alles)" },
  { v: "locatie", l: "Per locatie" },
  { v: "om", l: "Per OM" },
];

function AiAnalysis({ meta }) {
  const [level, setLevel] = useState("overall");
  const [target, setTarget] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const needsTarget = level === "locatie" || level === "om";
  const targetOptions = level === "locatie" ? (meta.locations || []) : level === "om" ? (meta.oms || []) : [];

  // Load last stored analysis for current level/target
  useEffect(() => {
    if (needsTarget && !target) { setAnalysis(null); return; }
    setFetching(true);
    const p = new URLSearchParams({ level });
    if (target) p.append("target", target);
    api.get(`/ai-analysis/latest?${p.toString()}`)
      .then((r) => setAnalysis(r.data.analysis))
      .catch(() => setAnalysis(null))
      .finally(() => setFetching(false));
  }, [level, target, needsTarget]);

  const generate = async () => {
    if (needsTarget && !target) { toast.error("Selecteer eerst een locatie of OM."); return; }
    setLoading(true);
    try {
      const r = await api.post("/ai-analysis", { level, target: target || null });
      setAnalysis(r.data);
      toast.success("AI-analyse gegenereerd");
    } catch (e) {
      err(e, "AI-analyse mislukt");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }) : ""; } catch { return iso; } };

  return (
    <div className={card} data-testid="ai-analyse">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-5 w-5 text-blue-600" />
        <h2 className={sectionTitle + " mb-0"}>AI-analyse</h2>
      </div>
      <p className={sectionDesc}>Herkent terugkerende afspraken, thema's en opvolg-patronen — met concrete adviezen. On-demand (Claude).</p>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Niveau</label>
          <select className={`${input} max-w-[170px]`} data-testid="ai-analyse-level" value={level}
            onChange={(e) => { setLevel(e.target.value); setTarget(""); }}>
            {AI_LEVELS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
          </select>
        </div>
        {needsTarget && (
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">{level === "locatie" ? "Locatie" : "OM"}</label>
            <select className={`${input} max-w-[180px]`} data-testid="ai-analyse-target" value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">— Kies {level === "locatie" ? "locatie" : "OM"} —</option>
              {targetOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        )}
        <button className={btnPrimary} data-testid="ai-analyse-generate" onClick={generate} disabled={loading || (needsTarget && !target)}>
          {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? "Analyseren…" : "Genereer analyse"}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-zinc-500" data-testid="ai-analyse-loading">
          <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-blue-600" />
          De AI analyseert de afspraken… dit kan even duren.
        </div>
      ) : fetching ? (
        <div className="text-center py-10 text-sm text-zinc-400">Laden…</div>
      ) : analysis ? (
        <div data-testid="ai-analyse-result" className="rounded-[10px] border border-zinc-200 bg-white p-5">
          <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-zinc-100">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-400" data-testid="ai-analyse-generated-at">
              Gegenereerd op {fmt(analysis.generatedAt)} · {analysis.model}
            </span>
            <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              {analysis.counts ? `${analysis.counts.afspraken} afspraken · ${analysis.counts.calls} calls` : ""}
            </span>
          </div>
          <div className="prose-sm max-w-none">{renderMarkdown(analysis.result)}</div>
        </div>
      ) : (
        <div className="text-center py-12 text-sm text-zinc-400 border border-dashed border-zinc-200 rounded-md">
          Nog geen analyse voor deze selectie. Klik op "Genereer analyse".
        </div>
      )}
    </div>
  );
}

function VerzoekenBeheer({ onApplied }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opmerking, setOpmerking] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/weekritme-requests")
      .then((r) => setRequests(r.data))
      .catch((e) => err(e, "Verzoeken laden mislukt"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const review = async (req, action) => {
    try {
      await api.patch(`/weekritme-requests/${req.id}`, { action, reviewOpmerking: opmerking[req.id] || "" });
      toast.success(action === "goedkeuren" ? "Verzoek goedgekeurd en toegepast" : "Verzoek afgewezen");
      load();
      if (action === "goedkeuren") onApplied && onApplied();
    } catch (e) {
      err(e, "Beoordelen mislukt");
    }
  };

  const open = requests.filter((r) => r.status === "open");
  const handled = requests.filter((r) => r.status !== "open");

  return (
    <div className={card} data-testid="verzoeken-beheer">
      <div className="flex items-center gap-2 mb-1">
        <GitPullRequest className="h-5 w-5 text-blue-600" />
        <h2 className={sectionTitle + " mb-0"}>Weekritme-verzoeken</h2>
      </div>
      <p className={sectionDesc}>Wijzigingen voorgesteld door OM's. Goedkeuren past de wijziging direct toe op het weekritme.</p>

      {loading ? (
        <div className="text-center py-8 text-sm text-zinc-400">Laden…</div>
      ) : (
        <>
          <h3 className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">Openstaand ({open.length})</h3>
          {open.length === 0 ? (
            <p className="text-sm text-zinc-400 mb-3 md:mb-4">Geen openstaande verzoeken.</p>
          ) : (
            <div className="space-y-3 mb-3 md:mb-4">
              {open.map((r) => (
                <VerzoekItem key={r.id} req={r} testid={`verzoek-${r.id}`}>
                  <div className="mt-3 pt-3 border-t border-zinc-100 space-y-2">
                    <input data-testid={`verzoek-opmerking-${r.id}`} className={input} placeholder="Opmerking (optioneel)"
                      value={opmerking[r.id] || ""} onChange={(e) => setOpmerking((o) => ({ ...o, [r.id]: e.target.value }))} />
                    <div className="flex gap-2">
                      <button data-testid={`verzoek-approve-${r.id}`} onClick={() => review(r, "goedkeuren")}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                        <Check className="h-4 w-4" /> Goedkeuren
                      </button>
                      <button data-testid={`verzoek-reject-${r.id}`} onClick={() => review(r, "afwijzen")}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-50">
                        <X className="h-4 w-4" /> Afwijzen
                      </button>
                    </div>
                  </div>
                </VerzoekItem>
              ))}
            </div>
          )}

          {handled.length > 0 && (
            <>
              <h3 className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">Historie ({handled.length})</h3>
              <div className="space-y-3">
                {handled.map((r) => <VerzoekItem key={r.id} req={r} testid={`verzoek-${r.id}`} />)}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}




